import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, CreditCard, Eye, EyeOff, HeartPulse, Loader2, LockKeyhole, ScanLine, ShieldCheck, Stethoscope, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg, ROLE_HOME } from "@/lib/api";
import { ErrorBox, Splash } from "@/components/common";

const PERKS = [
  { icon: Stethoscope, text: "Online consultations with GIA doctors" },
  { icon: HeartPulse, text: "Physical checkups & tracked benefits" },
  { icon: CreditCard, text: "One card, one account, all your care" },
];

const BrandPanel = () => (
  <aside className="relative hidden overflow-hidden bg-[#1b0f4d] lg:flex lg:w-[52%] lg:flex-col lg:justify-between lg:p-14" data-testid="login-brand-panel">
    <div className="pointer-events-none absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full bg-violet-600/40 blur-[120px]" />
    <div className="pointer-events-none absolute -bottom-40 right-0 h-[460px] w-[460px] rounded-full bg-blue-500/30 blur-[120px]" />
    <div className="pointer-events-none absolute right-24 top-1/3 h-40 w-40 rounded-full bg-cyan-400/20 blur-3xl" />
    <p className="relative text-xs font-bold uppercase tracking-[0.3em] text-violet-200/80">GIA Healthcare</p>
    <div className="relative">
      <div className="rise floaty inline-block rounded-[36px] bg-white/95 p-6 shadow-2xl shadow-violet-950/50">
        <img src="/logo-full.png" alt="GIA care+" className="w-[340px] xl:w-[400px]" />
      </div>
      <h2 className="rise rise-1 mt-10 max-w-md text-4xl font-extrabold leading-tight tracking-tight text-white">
        Care that travels <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">with you.</span>
      </h2>
      <ul className="mt-8 space-y-3">
        {PERKS.map(({ icon: I, text }, i) => (
          <li key={text} className={`rise rise-${i + 2} flex items-center gap-3 text-sm text-violet-100/90`}>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10 ring-1 ring-white/15"><I size={16} className="text-cyan-300" /></span>
            {text}
          </li>
        ))}
      </ul>
    </div>
    <p className="relative flex items-center gap-2 text-xs text-violet-200/60"><ShieldCheck size={14} /> Cards activate automatically once PayMongo confirms payment</p>
  </aside>
);

const Field = ({ icon: I, label, hint, children }) => (
  <label className="block">
    <span className="label">{label}</span>
    <div className="relative">
      <I size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      {children}
    </div>
    {hint && <span className="mt-1.5 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user === null) return <Splash />;
  if (user) return <Navigate to={user.must_change_password ? "/change-password" : ROLE_HOME[user.role]} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(username.trim(), password);
      const next = params.get("next");
      nav(u.must_change_password ? "/change-password" : next && next.startsWith("/") ? next : ROLE_HOME[u.role], { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <BrandPanel />
      <main className="relative flex flex-1 items-center justify-center overflow-hidden px-6 py-10">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-violet-200/50 blur-3xl lg:hidden" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-blue-200/50 blur-3xl lg:hidden" />
        <div className="relative w-full max-w-sm">
          <img src="/logo-full.png" alt="GIA care+" className="rise floaty mx-auto w-56 drop-shadow-xl lg:hidden" data-testid="brand-logo" />
          <div className="rise rise-1 mt-6 lg:mt-0">
            <p className="eyebrow">Sign in</p>
            <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h1>
            <p className="mt-2 text-sm text-slate-500">Use your card serial number and password.</p>
          </div>
          <form onSubmit={submit} className="rise rise-2 mt-8 space-y-5" data-testid="login-form">
            <Field icon={UserRound} label="Username" hint="Customers: the serial printed on your physical card">
              <input className="field pl-10 font-mono" autoComplete="username" placeholder="GIA-2026-000001" value={username}
                onChange={(e) => setUsername(e.target.value)} required data-testid="login-username-input" />
            </Field>
            <Field icon={LockKeyhole} label="Password">
              <input className="field pl-10 pr-10" type={show ? "text" : "password"} autoComplete="current-password" placeholder="••••••••" value={password}
                onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
              <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 transition-colors hover:text-slate-700"
                onClick={() => setShow((s) => !s)} aria-label="Toggle password visibility" data-testid="toggle-password-visibility">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </Field>
            <ErrorBox error={error} />
            <button className="btn-primary group w-full py-3.5" disabled={busy} data-testid="login-submit-button">
              {busy ? <Loader2 className="animate-spin" size={16} /> : null} Sign in
              {!busy && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
            </button>
            <p className="text-center text-[11px] text-slate-400" data-testid="forgot-password-note">Forgot your password? Contact GIA support for an administrator-assisted reset.</p>
          </form>
          <div className="rise rise-3 my-7 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-slate-300">
            <span className="h-px flex-1 bg-slate-200" /> New card? <span className="h-px flex-1 bg-slate-200" />
          </div>
          <Link to="/register" className="rise rise-3 group flex items-center gap-4 rounded-2xl border border-violet-100 bg-violet-50/60 p-4 transition-colors hover:bg-violet-50" data-testid="scan-your-card-button">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-lg shadow-violet-500/30"><ScanLine size={20} /></span>
            <span className="flex-1">
              <span className="block text-sm font-bold text-slate-900">Scan your card · Register</span>
              <span className="block text-xs text-slate-500">Set up your account after your card is activated</span>
            </span>
            <ArrowRight size={16} className="text-violet-500 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/enroll/gia-care-plus-annual" className="btn-ghost btn-sm mx-auto mt-4 flex w-fit" data-testid="view-packages-link">View GIA CARE+ packages</Link>
          <p className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-slate-400 lg:hidden"><ShieldCheck size={13} /> Secure sign-in · Cards activated after verified payment</p>
        </div>
      </main>
    </div>
  );
}
